// src/redux/MyCartSlice.js
import { createSlice } from "@reduxjs/toolkit";

/** Extrait l'image de l'article — même règle que la grille / fiche produit :
 *  ImgArt (TabStock.imgArt servie par l'API) ; RepXpert.Images n'est rempli qu'en recherche véhicule */
function getItemImage(item) {
  if (item?.ImgArt) return item.ImgArt;
  const first = (item?.RepXpert?.Images || []).find(Boolean);
  if (first) return typeof first === "string" ? first : first?.url || null;
  return null;
}

/** Normalise un article venant de ta trame */
function normalizeItem(item, dbName) {
  return {
    dbName: dbName || item?.dbName || "Amen",   // ✅ AJOUT
    CodArt: item?.CodArt ?? item?.RefArt ?? item?.TecDoc?.ArtNumber ?? "NA",
    LibArt: item?.LibArt ?? "Article",
    PrixTTC: Number(item?.PrixTTC || 0),
    RefArt: item?.RefArt ?? item?.TecDoc?.ArtNumber ?? "",
    Marque: item?.Marque ?? "",
    stockQt: Number(item?.Qt || 0),
    image: getItemImage(item),
    raw: item,
    tva: Number(item?.TVA ?? item?.Tva ?? item?.tva ?? 0),
    remise: Number(item?.Remise ?? item?.remise ??  0),
  };
}

const initialState = {
  items: [], // [{...normalized, qty:int}]
};

const findIndex = (state, cod) =>
  state.items.findIndex((x) => String(x.CodArt) === String(cod));

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    /** Ajoute un article (qty par défaut = 1). Si déjà présent => +qty */
    addToCart: (state, action) => {
       const { item, qty = 1, dbName } = action.payload; 
  const n = normalizeItem(item, dbName);            
      const idx = findIndex(state, n.CodArt);
      const q = Math.max(1, parseInt(qty, 10) || 1);
      if (idx >= 0) {
        state.items[idx].qty = Math.max(1, parseInt(state.items[idx].qty, 10) + q);
      } else {
        state.items.push({ ...n, qty: q });
      }
    },

    /** Quantité = entier ≥ 1 */
    setQty: (state, action) => {
      const { CodArt, qty } = action.payload;
      const idx = findIndex(state, CodArt);
      if (idx >= 0) {
        const q = Math.max(1, parseInt(qty, 10) || 1);
        state.items[idx].qty = q;
      }
    },

    /** +1 */
    increaseQty: (state, action) => {
      const CodArt = action.payload;
      const idx = findIndex(state, CodArt);
      if (idx >= 0) {
        state.items[idx].qty = Math.max(1, parseInt(state.items[idx].qty, 10) + 1);
      }
    },

    /** -1 (reste clampé à 1) */
    decreaseQty: (state, action) => {
      const CodArt = action.payload;
      const idx = findIndex(state, CodArt);
      if (idx >= 0) {
        const cur = Math.max(1, parseInt(state.items[idx].qty, 10) || 1);
        state.items[idx].qty = Math.max(1, cur - 1);
      }
    },

    /** Supprime la ligne */
    removeFromCart: (state, action) => {
      const CodArt = action.payload;
      const idx = findIndex(state, CodArt);
      if (idx >= 0) state.items.splice(idx, 1);
    },

    /** Vide le panier */
    clearCart: (state) => {
      state.items = [];
    },
  },
});

export const {
  addToCart,
  setQty,
  increaseQty,
  decreaseQty,
  removeFromCart,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;

/** Sélecteurs pratiques */
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotals = (state) => {
  const items = selectCartItems(state);

  const lines = items.map((it) => {
    const qty = Number(it.qty || 0);
    const prixTTCInitial = Number(it.PrixTTC || 0);
    
    // 1. Calcul de la remise sur le TTC
    const montantRemiseLigne = prixTTCInitial * (Number(it.remise || 0) / 100) * qty;
    const totalLigneTTC = (prixTTCInitial * qty) - montantRemiseLigne;

    // 2. Calcul du HT et de la TVA basé sur le taux spécifique de l'article
    // Formule : HT = TTC / (1 + (TauxTVA/100))
    const totalLigneHT = totalLigneTTC / (1 + (Number(it.tva || 19) / 100));
    const totalLigneTVA = totalLigneTTC - totalLigneHT;

    return {
      ...it,
      lineTTC: totalLigneTTC,
      lineHT: totalLigneHT,
      lineTVA: totalLigneTVA,
      lineRemise: montantRemiseLigne
    };
  });

  // Accumulation des totaux
  const subTTC = lines.reduce((a, b) => a + (Number(b.PrixTTC) * Number(b.qty)), 0);
  const totalRemise = lines.reduce((a, b) => a + b.lineRemise, 0);
  const totalTTC = lines.reduce((a, b) => a + b.lineTTC, 0);
  const totalHT = lines.reduce((a, b) => a + b.lineHT, 0);
  const totalTVA = lines.reduce((a, b) => a + b.lineTVA, 0);

  return { 
    lines, 
    subTTC,        // Total avant remise
    totalHT,       // Somme des HT calculés par ligne
    totalTVA,      // Somme des TVA calculées par ligne
    remise: totalRemise, 
    livraison: 0, 
    total: totalTTC // Net à payer (TTC - Remise)
  };
};
